import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CreditCard, Nfc, Loader2, CheckCircle2, AlertTriangle, ScanLine,
} from 'lucide-react';
import { HTTPError } from 'ky';
import {
  fetchMyRfidCards, fetchMyChargers, fetchMyPassListAccess,
  openRfidCaptureWindow, pollRfidCaptureWindow, confirmRfidCaptureWindow,
  cancelRfidCaptureWindow,
  type RfidCaptureWindow,
} from '@/lib/api';
import styles from './MyFobsPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Cards & fobs — the driver's RFID surface.
 *
 * Lists the driver's own tags and hosts the self-registration flow
 * (F3, Warren 2026-09-25: "Resident means 'at the charger.'"): plug in,
 * scan the charger's code or type the ID printed on it → the server
 * checks the charger is live and showing Preparing, opens a 3-minute
 * capture window and pins the fee, which is shown BEFORE the tap → tap
 * the fob → the captured tag comes back (masked) → confirm and pay from
 * your own saved card → the fob works; tap it again to start charging.
 *
 * Deep links: `?charger=<identity>` (from the scanner in fob mode) opens
 * the window straight away; `?window=<id>` (from the charging screen's
 * tap-first offer) loads an already-matched window at the paywall.
 *
 * The fee shown is pinned server-side when the window opens; the
 * Confirm tap is the consent that authorizes the charge (whoever
 * initiates, pays). Refusals on open carry a code: 403
 * FOB_REGISTRATION_CLOSED (the charger's building has not turned it on) or
 * FOB_REGISTRATION_PAUSED (the EVI-wide switch is off); 404 no such charger;
 * 409 FOB_NOT_PREPARING / FOB_CHARGER_OFFLINE / FOB_UNSUPPORTED_CHARGER, or
 * another driver holds this charger's window right now. The server's own
 * message is what the driver sees.
 */

type FlowStep = 'idle' | 'pick' | 'waiting' | 'matched' | 'done';

interface PickRow {
  chargerId: string;
  identity: string;
  name: string;
  locationName: string;
}

/** Same alphabet the server accepts: an OCPP identity typed as printed. */
const CHARGER_REF_RE = /^[A-Za-z0-9_-]{1,64}$/;

function parseErrorMessage(err: unknown, fallback: string): Promise<string> {
  if (err instanceof HTTPError) {
    return err.response.clone().json()
      .then((body: { error?: { message?: string } }) => body?.error?.message ?? fallback)
      .catch(() => fallback);
  }
  return Promise.resolve(fallback);
}

export function MyFobsPage({ onNavigate }: Props) {
  const { t } = useTranslation('rfid');
  const qc = useQueryClient();

  const cardsQ = useQuery({ queryKey: ['rfid', 'cards'], queryFn: fetchMyRfidCards });
  const myChargersQ = useQuery({ queryKey: ['myChargers'], queryFn: fetchMyChargers, staleTime: 60_000 });
  const passListQ = useQuery({ queryKey: ['myPassListAccess'], queryFn: fetchMyPassListAccess, staleTime: 60_000 });

  const [step, setStep] = useState<FlowStep>('idle');
  const [win, setWin] = useState<RfidCaptureWindow | null>(null);
  const [flowError, setFlowError] = useState('');
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [typedId, setTypedId] = useState('');
  // How the flow ended: a live fob, or a PENDING one (no grant here, either
  // order - it switches on at the next sighting: tap, then start from the app).
  const [doneKind, setDoneKind] = useState<'live' | 'pending'>('live');
  const winRef = useRef<RfidCaptureWindow | null>(null);
  winRef.current = win;
  const stepRef = useRef<FlowStep>(step);
  stepRef.current = step;

  // Poll while waiting: the server owns matching, expiry and
  // ambiguity voiding — the client only reflects window status.
  useEffect(() => {
    if (step !== 'waiting' || !win) return;
    const tick = window.setInterval(() => {
      const w = winRef.current;
      if (!w) return;
      setSecondsLeft(Math.max(0, Math.round((new Date(w.expires_at).getTime() - Date.now()) / 1000)));
      pollRfidCaptureWindow(w._id)
        .then((next) => {
          // A slow in-flight poll can resolve AFTER a faster one
          // already advanced us to 'matched'; its stale 'open' body
          // would blank the matched tag. Drop it.
          if (next.status === 'open' && stepRef.current !== 'waiting') return;
          setWin(next);
          if (next.status === 'matched') setStep('matched');
          else if (next.status === 'cancelled' && next.own_pending) {
            // The driver tapped their OWN pending fob. A tap here does not
            // switch it on (only a charging session does); say how.
            setStep('idle');
            setFlowError(t('flow.ownPending'));
          } else if (next.status !== 'open') {
            // expired / void_ambiguous / cancelled — surface and reset.
            setStep('idle');
            setFlowError(next.status === 'void_ambiguous' ? t('flow.ambiguous') : t('flow.expired'));
          }
        })
        .catch(() => { /* transient poll failure — next tick retries */ });
    }, 2500);
    return () => window.clearInterval(tick);
  }, [step, win?._id, t]);

  const pickRows: PickRow[] = [
    ...(myChargersQ.data ?? []).map((c) => ({
      chargerId: c._id,
      identity: c.identity,
      name: c.display_name || c.identity,
      locationName: c.location_name,
    })),
    ...(passListQ.data ?? [])
      .filter((r) => !(myChargersQ.data ?? []).some((c) => c._id === r.charger_id))
      .map((r) => ({
        chargerId: r.charger_id,
        identity: r.identity,
        name: r.identity,
        locationName: r.location_name,
      })),
  ];

  const startAt = async (charger: string): Promise<void> => {
    setBusy(true);
    setFlowError('');
    try {
      const w = await openRfidCaptureWindow(charger);
      setWin(w);
      setSecondsLeft(Math.max(0, Math.round((new Date(w.expires_at).getTime() - Date.now()) / 1000)));
      setStep(w.status === 'matched' ? 'matched' : 'waiting');
    } catch (err) {
      setFlowError(await parseErrorMessage(err, t('flow.openFailed')));
      setStep('idle');
    } finally {
      setBusy(false);
    }
  };

  // Deep links, once at mount: a scanned charger opens the window; an
  // accepted charging-screen offer lands on its matched window.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current) return;
    deepLinked.current = true;
    const q = new URLSearchParams(window.location.search);
    const charger = q.get('charger');
    const windowId = q.get('window');
    if (windowId) {
      setBusy(true);
      pollRfidCaptureWindow(windowId)
        .then((w) => {
          setWin(w);
          setStep(w.status === 'matched' ? 'matched' : 'idle');
          if (w.status !== 'matched') setFlowError(t('flow.loadFailed'));
        })
        .catch(() => setFlowError(t('flow.loadFailed')))
        .finally(() => setBusy(false));
    } else if (charger && CHARGER_REF_RE.test(charger)) {
      void startAt(charger);
    }
  }, []);

  const submitTyped = (): void => {
    const ref = typedId.trim();
    if (!CHARGER_REF_RE.test(ref)) {
      setFlowError(t('pick.invalidId'));
      return;
    }
    void startAt(ref);
  };

  const confirm = async (): Promise<void> => {
    if (!win) return;
    setBusy(true);
    setFlowError('');
    try {
      const res = await confirmRfidCaptureWindow(win._id);
      setDoneKind(res.pending ? 'pending' : 'live');
      setStep('done');
      void qc.invalidateQueries({ queryKey: ['rfid', 'cards'] });
    } catch (err) {
      setFlowError(await parseErrorMessage(err, t('flow.confirmFailed')));
      // The server may have moved the window to a terminal state
      // (confirm TTL expired, tag claimed elsewhere, site disabled) —
      // a dead Confirm button that 409s forever is worse than
      // resetting. 402 (add a payment method) keeps the matched panel
      // so the driver can add a card and retry. A still-matched window is
      // re-read so a server-side flip to pending (FOB_NOW_PENDING, nothing
      // charged) shows the pending notice before they confirm again.
      try {
        const fresh = await pollRfidCaptureWindow(win._id);
        if (fresh.status !== 'matched') {
          setWin(null);
          setStep('idle');
        } else {
          setWin(fresh);
        }
      } catch { /* keep current state; the error line is showing */ }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (): Promise<void> => {
    if (win) void cancelRfidCaptureWindow(win._id).catch(() => undefined);
    setWin(null);
    setStep('idle');
    setFlowError('');
  };

  const fee = win ? (win.amount_cents / 100).toFixed(2) : null;

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}><Nfc size={20} /></div>
        <div>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle')}</p>
        </div>
      </header>

      {/* ── Existing fobs ─────────────────────────────────────────── */}
      {cardsQ.isLoading && <p className={styles.loading}>{t('loading')}</p>}
      {cardsQ.data && cardsQ.data.length > 0 && (
        <div className={styles.cardList}>
          {cardsQ.data.map((c) => (
            <div key={c._id} className={styles.cardRow}>
              <CreditCard size={18} className={styles.cardIcon} />
              <div className={styles.cardText}>
                <p className={styles.cardTag}>{c.label || c.tag_uid}</p>
                {c.label && <p className={styles.cardMeta}>{c.tag_uid}</p>}
              </div>
              <span className={c.is_active ? styles.badgeActive : styles.badgeInactive}>
                {c.is_active ? t('active') : c.pending ? t('pending') : t('inactive')}
              </span>
            </div>
          ))}
        </div>
      )}
      {cardsQ.data && cardsQ.data.length === 0 && step === 'idle' && (
        <p className={styles.emptyLine}>{t('empty')}</p>
      )}

      {flowError && step !== 'done' && (
        <div className={styles.flowError}><AlertTriangle size={16} /> {flowError}</div>
      )}

      {/* ── Flow ──────────────────────────────────────────────────── */}
      {step === 'idle' && (
        <button className={styles.primaryBtn} onClick={() => { setFlowError(''); setStep('pick'); }}>
          {t('addButton')}
        </button>
      )}

      {step === 'pick' && (
        <div className={styles.panel}>
          <p className={styles.panelTitle}>{t('pick.title')}</p>
          <p className={styles.panelBody}>{t('pick.bodyPresence')}</p>
          {/* Presence is the proof: the charger the driver is AT, from its
              code or its printed ID - on screen whether or not it is in
              their list. The server re-checks live + Preparing. */}
          <button className={styles.primaryBtn} disabled={busy} onClick={() => onNavigate('/scan?for=fob')}>
            <ScanLine size={16} /> {t('pick.scan')}
          </button>
          <form
            className={styles.pickList}
            onSubmit={(e) => { e.preventDefault(); submitTyped(); }}
          >
            <label className={styles.panelBody} htmlFor="fob-charger-id">{t('pick.enterLabel')}</label>
            <input
              id="fob-charger-id"
              className={styles.pickRow}
              value={typedId}
              onChange={(e) => setTypedId(e.target.value)}
              placeholder={t('pick.enterPlaceholder')}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
            />
            <button type="submit" className={styles.ghostBtn} disabled={busy || !typedId.trim()}>
              {t('pick.continue')}
            </button>
          </form>
          {pickRows.length > 0 && <p className={styles.panelBody}>{t('pick.yourChargers')}</p>}
          <div className={styles.pickList}>
            {pickRows.map((r) => (
              <button
                key={r.chargerId}
                className={styles.pickRow}
                disabled={busy}
                onClick={() => void startAt(r.identity || r.chargerId)}
              >
                <span className={styles.pickName}>{r.name}</span>
                <span className={styles.pickMeta}>{r.locationName}</span>
              </button>
            ))}
          </div>
          <button className={styles.ghostBtn} onClick={() => setStep('idle')}>{t('cancel')}</button>
        </div>
      )}

      {step === 'waiting' && win && (
        <div className={styles.panel}>
          <Loader2 size={26} className={styles.spin} />
          <p className={styles.panelTitle}>{t('flow.waitingTitle')}</p>
          <p className={styles.panelBody}>
            {t('flow.waitingBody', { identity: win.charger_identity })}
          </p>
          {/* Fee disclosed BEFORE anything is tapped or confirmed —
              the window response pins the site's fee at open, and
              nothing is charged until the explicit confirm. */}
          {win.amount_cents > 0 && (
            <p className={styles.panelBody}>
              {t('flow.feeNotice', { fee: (win.amount_cents / 100).toFixed(2) })}
            </p>
          )}
          <p className={styles.countdown}>{t('flow.secondsLeft', { count: secondsLeft })}</p>
          <button className={styles.ghostBtn} onClick={() => void cancel()}>{t('cancel')}</button>
        </div>
      )}

      {step === 'matched' && win && (
        <div className={styles.panel}>
          <Nfc size={26} className={styles.matchedIcon} />
          <p className={styles.panelTitle}>{t('flow.matchedTitle')}</p>
          <p className={styles.tagChip}>{win.matched_tag}</p>
          {win.matched_via === 'session_offer' && (
            <p className={styles.panelBody}>{t('flow.tappedBefore')}</p>
          )}
          {/* Said BEFORE the driver pays: a no-grant registration switches on
              at the next sighting, not now. */}
          {win.pending_on_bind && (
            <p className={styles.panelBody}>{t('flow.pendingNotice')}</p>
          )}
          <p className={styles.panelBody}>
            {win.amount_cents > 0
              ? t('flow.feeLine', { fee })
              : t(win.pending_on_bind ? 'flow.freeLinePending' : 'flow.freeLine')}
          </p>
          <button className={styles.primaryBtn} disabled={busy} onClick={() => void confirm()}>
            {busy
              ? t('flow.confirming')
              : win.amount_cents > 0
                ? t('flow.confirmPay', { fee })
                : t(win.pending_on_bind ? 'flow.confirmFreePending' : 'flow.confirmFree')}
          </button>
          <button className={styles.ghostBtn} disabled={busy} onClick={() => void cancel()}>{t('cancel')}</button>
        </div>
      )}

      {step === 'done' && (
        <div className={styles.panel}>
          <CheckCircle2 size={26} className={styles.doneIcon} />
          <p className={styles.panelTitle}>{t('flow.doneTitle')}</p>
          <p className={styles.panelBody}>{t(
            doneKind === 'pending' ? 'flow.doneBodyPending'
              : win?.matched_via === 'session_offer' ? 'flow.doneBodySession' : 'flow.doneBody',
          )}</p>
          <button className={styles.primaryBtn} onClick={() => { setWin(null); setStep('idle'); }}>
            {t('flow.doneClose')}
          </button>
        </div>
      )}
    </div>
  );
}
